/**
 * M7 S37: Mission 2's world content (plan S37; spec §5, §17.7; D-422 T6, D-425 V-1).
 *
 * - **Site keys** (`resolveM12Hint`, `resolveStoryCommand`): the mission data's `spawnStoryShip` commands name
 *   `site:<hint>` systems; the story service resolves them before the worker sees them (`site:column-nine` → the
 *   Column Nine system, `site:m2-barge` → the barge system: the guardian-base system's lowest-id neighbour over relays,
 *   spec §5 "the next system"). An unresolved hint stays as it is and the worker refuses it (`unknownSystem`).
 * - **The courier leg's patrol scan** (`courierScan`): `contrabandScan` with the T6 Holdfast exemption (D-422).
 * - **The Watch rows** (`watchRows`, V-1): livery, hull class, place and hours, generated only from the player's
 *   real `career.tetherSales`; another lessee's row when there is no sale. Never the registration, never a colour
 *   overlay.
 * - **The barge trace** (`recordM2Trace`, `bargeTrace`) and **Watch book 7's d121 row** after the barge scene
 *   (`bookSevenRows`).
 * - **The meter reward** (`grantResonanceMeter`, `resonanceMeterFitted`): the resonance meter utility module
 *   (`modules.json utility.resonanceMeter`) is fitted on the flown ship when the barge trace is first recorded after
 *   help or stand off, never after a betrayal. It is owned per ship (`ShipState.resonanceMeter`, carried by the
 *   hangar entry on a switch); the HUD shows readings only while the flown ship has it. A career from before the
 *   module (the reward flag, no trace) gets it on its next story result or load (`meterRewardOwed`).
 *
 * Event-driven, never per frame; it may allocate. Tuning in `story/world12.json`.
 */
import type { Career } from '../../career/types'
import type { Galaxy } from '../../galaxy/types'
import { contrabandScan, m12Config, type ContrabandScan, type M12Config } from '../../story/m12'
import { hasFlag, storyOf, withStory } from '../../story/state'
import type { StoryTraces } from '../../story/types'
import type { FactionId, StrategicCommand } from '../../strategic/types'
import { anchorSystem, resolveSystemHint, type StorySites } from '../sites'
import { world12Config, type World12Config } from './m1'

/** The barge raid's system: the guardian-base system's lowest-id neighbour over relays (the base itself when alone). */
export function bargeSystem(galaxy: Galaxy): string | null {
  const base = anchorSystem(galaxy, 'guardian-base')
  if (base === undefined) return null
  let best: string | null = null
  for (const r of galaxy.relays) {
    const other = r.a === base.id ? r.b : r.b === base.id ? r.a : null
    if (other !== null && other !== base.id && (best === null || other < best)) best = other
  }
  return best ?? base.id
}

/** A story hint as a system id, with the M1/M2 sites (`m2-barge`) on top of S30's `resolveSystemHint`. */
export function resolveM12Hint(galaxy: Galaxy, sites: StorySites, hint: string): string | null {
  if (hint === 'm2-barge') return bargeSystem(galaxy)
  return resolveSystemHint(galaxy, sites, hint)
}

const SITE = 'site:'

/** A worker command with its `site:<hint>` system resolved (unchanged when it has none or the hint is unknown). */
export function resolveStoryCommand(cmd: StrategicCommand, resolve: (hint: string) => string | null): StrategicCommand {
  if (cmd.type !== 'spawnStoryShip' || !cmd.systemId.startsWith(SITE)) return cmd
  const id = resolve(cmd.systemId.slice(SITE.length))
  return id === null ? cmd : { ...cmd, systemId: id }
}

// ---------------------------------------------------------------------------------------------
// The courier leg (T6, D-422)
// ---------------------------------------------------------------------------------------------

export type CourierScan = ContrabandScan | { kind: 'exempt' }

/**
 * A finished patrol scan on the courier leg. Holdfast patrols never fine Kell's own package (D-422); everywhere
 * else the fine (capped at the wallet, never confiscation) or the Authority wave-through stands.
 */
export function courierScan(career: Career, patrolFaction: FactionId, cfg: M12Config = m12Config()): CourierScan {
  if (patrolFaction === 'holdfast' && kellPackageOnly(career)) return { kind: 'exempt' }
  return contrabandScan(career, cfg)
}

/** The only contraband story item the player has is Kell's package. */
function kellPackageOnly(career: Career): boolean {
  let pkg = false
  for (const it of storyOf(career).items) {
    if (it.state !== 'held' && it.state !== 'copy' && it.state !== 'withheld') continue
    if (it.contraband !== true) continue
    if (it.id !== 'kell_package') return false
    pkg = true
  }
  return pkg
}

// ---------------------------------------------------------------------------------------------
// The Watch rows (V-1, spec §17.7)
// ---------------------------------------------------------------------------------------------

/** What the Watch could see of the player's hull: the actual paint and the hull class (never the registration). */
export interface WatchHull {
  livery: string
  hullClass: string
}

/** A ship def as the Watch saw it: the livery its faction paints (`world12.json m2.liveries`) and the hull class (the
 * name's bracketed class, else the name). */
export function watchHull(def: { name: string; faction: string } | undefined, cfg: World12Config = world12Config()): WatchHull {
  const m = cfg.m2
  if (def === undefined) return { livery: m.liveryFallback, hullClass: m.fallbackRow.hull }
  const open = def.name.lastIndexOf('(')
  const close = def.name.lastIndexOf(')')
  const hullClass = open >= 0 && close > open + 1 ? def.name.slice(open + 1, close) : def.name
  return { livery: m.liveries[def.faction] ?? m.liveryFallback, hullClass }
}

export interface WatchRowNames {
  /** A station's display name (the place of the sale). */
  station(stationId: string): string
}

/** Rows of the Watch overlay: [day, livery, hull class, place and hours]; at most 4 columns (D-425 HUD limits). */
export function watchRows(career: Career, hull: WatchHull, names: WatchRowNames, cfg: World12Config = world12Config()): string[][] {
  const m = cfg.m2
  const sales = career.tetherSales ?? []
  const rows: string[][] = []
  for (let i = sales.length - 1; i >= 0 && rows.length < m.watchRows; i--) {
    const s = sales[i]
    if (s.bunker === true || s.tonnes <= 0) continue
    const hours = Math.min(m.maxHours, Math.max(m.minHours, Math.round(s.tonnes * m.hoursPerTonne)))
    rows.push([`${m.dayPrefix}${s.day}`, hull.livery, hull.hullClass, `${names.station(s.stationId)}, ${hours} h`])
  }
  if (rows.length > 0) return rows
  const f = m.fallbackRow
  return [[f.day, f.livery, f.hull, `${m.place}, ${f.hours}`]]
}

// ---------------------------------------------------------------------------------------------
// The barge trace and book 7's d121 row
// ---------------------------------------------------------------------------------------------

/**
 * The M2 trace from the barge fork's flags, recorded once with today's online day. Recording a `wreck` trace (help or
 * stand off) also fits the meter reward (`grantResonanceMeter`): the trace marks the reward as given, so a meter lost
 * with its ship is never granted twice.
 */
export function recordM2Trace(career: Career): Career {
  const s = storyOf(career)
  if (s.traces?.barge !== undefined) return career
  const kind = hasFlag(career, 'watchers_betrayed') ? 'rig' : hasFlag(career, 'watchers_helped') || hasFlag(career, 'watchers_stood_off') ? 'wreck' : null
  if (kind === null) return career
  const traces: StoryTraces = { ...s.traces, barge: { kind, onlineDay: s.onlineDays } }
  const traced = withStory(career, (st) => ({ ...st, traces }))
  return kind === 'wreck' ? grantResonanceMeter(traced) : traced
}

/** The barge after the scene: a wreck (a salvage POI), a working rig, or null before the fork. */
export function bargeTrace(career: Career): 'wreck' | 'rig' | null {
  return storyOf(career).traces?.barge?.kind ?? null
}

/** Watch book 7's rows: the data's rows, plus the d121 barge row once the barge scene is over (a book update). */
export function bookSevenRows(base: readonly (readonly string[])[], career: Career, cfg: World12Config = world12Config()): readonly (readonly string[])[] {
  const t = bargeTrace(career)
  return t === null ? base : [...base, cfg.m2.bargeRow[t]]
}

// ---------------------------------------------------------------------------------------------
// The meter reward
// ---------------------------------------------------------------------------------------------

/** The resonance meter utility module is fitted on the flown ship (the HUD reads it every frame; allocation-free). */
export function resonanceMeterFitted(career: Career | null): boolean {
  return career?.ship.resonanceMeter === true
}

/** Fits the resonance meter on the flown ship (the same Career when it is already fitted there). */
export function grantResonanceMeter(career: Career): Career {
  if (career.ship.resonanceMeter === true) return career
  return { ...career, ship: { ...career.ship, resonanceMeter: true } }
}

/** Fits (`true`) or removes (`false`) the resonance meter on the flown ship (the dev hook; the same Career when unchanged). */
export function withResonanceMeter(career: Career, fitted: boolean): Career {
  if (fitted) return grantResonanceMeter(career)
  if (career.ship.resonanceMeter !== true) return career
  const ship = { ...career.ship }
  delete ship.resonanceMeter
  return { ...career, ship }
}

/**
 * A Career that earned the meter (help or stand off, no betrayal) before the module existed: the reward flag is set
 * but the barge trace, which marks the grant, is not. `recordM2Trace` fits it. Allocation-free (checked on load and on
 * every Career change).
 */
export function meterRewardOwed(career: Career): boolean {
  const s = career.story
  if (s === undefined || s.traces?.barge !== undefined) return false
  const f = s.flags
  if (f.watchers_betrayed === true) return false
  return f.watchers_helped === true || f.watchers_stood_off === true
}
